import React, { useCallback, useEffect, useState } from "react";

import ReaderAdvanced from "../tickets/ReaderAdvanced";
import ReaderPicker, { DisabledReaderNote } from "../tickets/ReaderPicker";
import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import {
  type ReaderStatus,
  describeJob,
  fetchReaderStatus,
  readersOf,
} from "../../services/readerApi";
import { useReaderJob } from "../../hooks/useReaderJob";
import { useSelectedReader } from "../../hooks/useSelectedReader";

/**
 * RFID reader card for the box-detail page (reached from the map).
 *
 * Health at a glance, a test scan, and — behind the collapsed "Advanced"
 * disclosure — the same hardware controls the ticket panel offers: RF power,
 * antenna-detect threshold, reset, bridge discovery + re-address (which is
 * where you change the reader's IP), and "Check antennas" / "Update antennas
 * on the box".
 *
 * Those writes used to be ticket-only, on the reasoning that they belong to a
 * job someone is working rather than a glance at a map pin. That was wrong in
 * practice (Jason 2026-09-17): the box-detail page is where an operator
 * actually lands when a box is reading short, and requiring a ticket first to
 * fix an antenna map or a moved reader IP meant the tools could not be found.
 * The panel is the SAME component, so the two surfaces cannot drift, and every
 * control keeps its own confirm + server-side kill switch.
 *
 * FORCE scan is still ticket-only — it is 5-10 minutes of a box not scanning,
 * which is a job, not a glance.
 *
 * **A box can carry two or three readers.** They are separate hardware on
 * separate addresses, and one can be dead or switched off while the others
 * scan — so this shows a picker and every action below targets the selected
 * reader. Showing one reader as "the reader" on such a box hides a dead one
 * in one direction and invents a fault in the other.
 *
 * The two status lines are separate on purpose and are the most useful
 * diagnostic the protocol offers: the tunnel opening proves the BRIDGE is
 * alive, and only a reply proves the READER behind it is. Bridge up with a
 * dead reader is a real and common state that otherwise looks like a network
 * problem.
 */
interface ReaderCardProps {
  scuName: string;
}

const dot = (ok: boolean | undefined): string =>
  ok === undefined
    ? "bg-secondary-300"
    : ok
      ? "bg-success-500"
      : "bg-error-500";

export const ReaderCard: React.FC<ReaderCardProps> = ({ scuName }) => {
  const { token, can } = useAuth();
  const canOperate = can("operator.triage");
  const [status, setStatus] = useState<ReaderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback((): void => {
    if (!token) return;
    setLoading(true);
    fetchReaderStatus(token, scuName)
      .then((s) => {
        setStatus(s);
        setLoadError(null);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [token, scuName]);

  useEffect(() => {
    load();
  }, [load]);

  const { job, busy, error, runTestScan } = useReaderJob(
    token,
    scuName,
    status?.latest_job ?? null,
  );

  // Which reader the test scan and the Advanced panel act on.
  const readers = readersOf(status);
  const { selectedId, selected, select } = useSelectedReader(readers);

  // Refresh the health lines once a scan finishes — a scan that just worked is
  // itself evidence the reader is fine.
  const jobDone = job && !job.is_running;
  useEffect(() => {
    if (jobDone) load();
  }, [jobDone, job?.job_id]);

  // Operators only. A customer looking at their own box has no business
  // driving its reader, and the server enforces the same rule.
  if (!canOperate) return null;

  return (
    <div className="bg-white rounded-lg shadow p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-secondary-900">RFID reader</h3>
        <button
          type="button"
          onClick={load}
          className="text-xs text-primary-600 hover:underline"
        >
          refresh
        </button>
      </div>

      {loading && !status && (
        <div className="text-sm text-secondary-500">checking the reader…</div>
      )}

      {loadError && (
        <div className="text-xs text-error-700 bg-error-50 border border-error-200 px-3 py-2 rounded mb-3">
          {loadError}
        </div>
      )}

      {status && !status.configured && (
        <div className="text-sm text-secondary-600">
          No reader is configured on this box.
          {status.error ? (
            <div className="text-xs text-secondary-500 mt-1">{status.error}</div>
          ) : null}
        </div>
      )}

      {status?.configured && (
        <>
          {/* Renders nothing on a single-reader box. */}
          <ReaderPicker
            readers={readers}
            selected={selectedId}
            onSelect={select}
            disabled={busy || (job?.is_running ?? false)}
          />

          {selected && !selected.is_active && <DisabledReaderNote reader={selected} />}

          <dl className="text-sm space-y-1 mb-3">
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${dot(selected?.bridge_reachable)}`}
              />
              <dt className="text-secondary-500 w-24">Bridge</dt>
              <dd className="text-secondary-900">
                {selected?.ip_address}:{selected?.port}{" "}
                {selected?.is_active
                  ? selected?.bridge_reachable
                    ? "reachable"
                    : "unreachable"
                  : "not probed"}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-block w-2 h-2 rounded-full ${dot(selected?.reader_responding)}`}
              />
              <dt className="text-secondary-500 w-24">Reader</dt>
              <dd className="text-secondary-900">
                {!selected?.is_active
                  ? "switched off at the box"
                  : selected?.reader_responding
                    ? `responding${selected.firmware ? ` · fw ${selected.firmware}` : ""}`
                    : "not responding"}
              </dd>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2" />
              <dt className="text-secondary-500 w-24">Antennas</dt>
              <dd className="text-secondary-900">
                {selected?.antenna_count} enabled
                {selected?.total_ports ? ` of ${selected.total_ports} ports` : ""}
              </dd>
            </div>
            {(selected?.temperature_c != null || selected?.power_summary) && (
              <div className="flex items-center gap-2">
                <span className="inline-block w-2 h-2" />
                <dt className="text-secondary-500 w-24">Radio</dt>
                <dd className="text-secondary-900">
                  {selected?.power_summary ?? "—"}
                  {selected?.temperature_c != null ? ` · ${selected.temperature_c}°C` : ""}
                </dd>
              </div>
            )}
          </dl>

          {/* Bridge up + reader silent is the case operators most often
              misread as a network fault, so name it explicitly. */}
          {selected?.is_active &&
            selected.bridge_reachable &&
            !selected.reader_responding && (
              <div className="text-xs text-warning-800 bg-warning-50 border border-warning-200 px-3 py-2 rounded mb-3">
                The bridge answered but reader {selected.reader_id} behind it did not.
                That is a reader fault, not a network one — the connection itself is
                fine.
              </div>
            )}

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={() => void runTestScan(undefined, selectedId ?? undefined)}
              disabled={busy || (job?.is_running ?? false)}
            >
              {job?.is_running
                ? "Scanning…"
                : `Run test scan${readers.length > 1 && selectedId != null ? ` · reader ${selectedId}` : ""}`}
            </Button>
            <span className="text-xs text-secondary-500">
              pauses the box&apos;s scanner, reads the reader, then restarts it
            </span>
          </div>

          {error && (
            <div className="text-xs text-error-700 mt-2">{error}</div>
          )}

          {job && <ReaderJobLine job={job} />}

          {/* Hardware writes. Collapsed by default (the component owns that
              state), operator-gated above, and each control re-checks the
              server's kill switches on press rather than pre-disabling —
              flipping a flag in app_config takes effect with no redeploy. */}
          <ReaderAdvanced
            scuName={scuName}
            status={status}
            reader={selected}
            disabled={busy || (job?.is_running ?? false)}
            onDone={() => load()}
          />
        </>
      )}
    </div>
  );
};

/** One-line job outcome, with the zero-tag case called out rather than shown
 *  as a bare "0 tags" that reads like a failure. */
const ReaderJobLine: React.FC<{ job: NonNullable<ReturnType<typeof useReaderJob>["job"]> }> = ({
  job,
}) => {
  const tone = job.is_running
    ? "text-secondary-600"
    : job.status === "completed"
      ? job.read_nothing
        ? "text-warning-800"
        : "text-success-700"
      : "text-error-700";
  const kind = job.kind === "force_scan" ? "Force scan" : "Test scan";
  return (
    <div className={`text-xs mt-3 ${tone}`}>
      <span className="font-medium">{kind}:</span> {describeJob(job)}
      {job.result?.antennas && job.result.antennas.length > 1 && !job.is_running && (
        <div className="mt-1 text-secondary-600">
          {/* Port numbers restart at 1 on every reader, so a bare "ant 4" is
              ambiguous on a multi-reader box. */}
          {job.result.reader_id != null ? `reader ${job.result.reader_id} · ` : ""}
          {job.result.antennas
            .filter((a) => a.unique_epcs > 0)
            .map((a) => `ant ${a.antenna}: ${a.unique_epcs}`)
            .join(" · ") || "no antenna read a tag"}
        </div>
      )}
      {job.result?.warning && (
        <div className="mt-1 text-warning-800">{job.result.warning}</div>
      )}
    </div>
  );
};

export default ReaderCard;
